import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Edit2, Package } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

export default function SKUsPage() {
  const { getValues, getItems } = useConfig(ALL_CONFIG_GROUPS);
  const SKU_STATUSES = getValues("sku_statuses");
  const PACK_FORMATS = getValues("pack_formats");
  const WEIGHT_UNITS = getValues("weight_units");
  const GST_RATES_LIST = getItems("gst_rates").map(i => parseInt(i.metadata?.rate ?? i.key));
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [form, setForm] = useState({
    name: "", short_name: "", description: "", category: "", sub_category: "",
    brand: "", packaging_type: "", pack_size: "", units_per_case: 0,
    mrp: 0, base_cost: 0, hsn_code: "", gst_percent: 18, shelf_life_days: 0,
    reorder_level: 0, moq: 1, status: "Active", barcode: "", tags: []
  });

  const loadSkus = async () => {
    try {
      const { data } = await api.get("/skus", { params: { search: search || undefined } });
      setSkus(data);
    } catch (err) { console.error("API get /skus error:", err); } finally { setLoading(false); }
  };

  useEffect(() => { loadSkus(); }, [search]);

  const openCreate = () => {
    setEditingSku(null);
    setForm({ name: "", short_name: "", description: "", category: "", sub_category: "", brand: "", packaging_type: "", pack_size: "", units_per_case: 0, mrp: 0, base_cost: 0, hsn_code: "", gst_percent: 18, shelf_life_days: 0, reorder_level: 0, moq: 1, status: "Active", barcode: "", tags: [] });
    setShowDialog(true);
  };

  const openEdit = (sku) => {
    setEditingSku(sku);
    setForm({
      name: sku.name, short_name: sku.short_name || "", description: sku.description || "",
      category: sku.category || "", sub_category: sku.sub_category || "",
      brand: sku.brand || "", packaging_type: sku.packaging_type || "",
      pack_size: sku.pack_size || "", units_per_case: sku.units_per_case || 0,
      mrp: sku.mrp || 0, base_cost: sku.base_cost || 0,
      hsn_code: sku.hsn_code || "", gst_percent: sku.gst_percent ?? 18,
      shelf_life_days: sku.shelf_life_days || 0, reorder_level: sku.reorder_level || 0,
      moq: sku.moq || 1, status: sku.status || "Active",
      barcode: sku.barcode || "", tags: sku.tags || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingSku) {
        await api.put(`/skus/${editingSku._id}`, form);
        toast.success("SKU updated");
      } else {
        await api.post("/skus", form);
        toast.success("SKU created");
      }
      setShowDialog(false);
      loadSkus();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(n || 0);

  return (
    <div data-testid="skus-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">SKU Master</h1>
          <p className="text-sm text-zinc-500 mt-1">Product catalogue management</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-sku-button">
          <Plus size={14} /> Add SKU
        </Button>
      </div>

      <div className="mb-4">
        <div className="relative w-full max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search SKUs..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="skus-search-input" />
        </div>
      </div>

      <div className="border border-zinc-200 overflow-x-auto">
        <table className="data-table w-full" data-testid="skus-table">
          <thead>
            <tr><th>SKU ID</th><th>Name</th><th>Category</th><th>Brand</th><th className="num">MRP</th><th className="num">GST %</th><th className="num">Reorder Lvl</th><th>Status</th><th className="w-16">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            skus.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">No SKUs found</td></tr> :
            skus.map(sku => (
              <tr key={sku._id} data-testid={`sku-row-${sku._id}`}>
                <td className="font-mono text-xs text-zinc-500">{sku.sku_id}</td>
                <td className="font-medium text-zinc-900">{sku.name}</td>
                <td>{sku.category || '-'}</td>
                <td>{sku.brand || '-'}</td>
                <td className="num font-mono">{fmt(sku.mrp)}</td>
                <td className="num font-mono">{sku.gst_percent}%</td>
                <td className="num font-mono">{sku.reorder_level}</td>
                <td><span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm ${sku.status === 'Active' ? 'status-active' : sku.status === 'Seasonal' ? 'status-warning' : 'status-inactive'}`}>{sku.status}</span></td>
                <td><button onClick={() => openEdit(sku)} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={13} className="text-zinc-500" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sku-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingSku ? "Edit SKU" : "Create SKU"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Product Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="sku-name-input" /></div>
              <div><Label className="text-xs">Short Name</Label><Input value={form.short_name} onChange={(e) => setForm({...form, short_name: e.target.value})} className="mt-1 rounded-sm" /></div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="sku-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PACK_FORMATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Sub-Category</Label><Input value={form.sub_category} onChange={(e) => setForm({...form, sub_category: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Brand</Label><Input value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} className="mt-1 rounded-sm" data-testid="sku-brand-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Packaging</Label>
                <Select value={form.packaging_type} onValueChange={(v) => setForm({...form, packaging_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{WEIGHT_UNITS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Pack Size</Label><Input value={form.pack_size} onChange={(e) => setForm({...form, pack_size: e.target.value})} placeholder="e.g. 500g" className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Units/Case</Label><Input type="number" value={form.units_per_case} onChange={(e) => setForm({...form, units_per_case: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">MRP (INR)</Label><Input type="number" step="0.01" value={form.mrp} onChange={(e) => setForm({...form, mrp: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="sku-mrp-input" /></div>
              <div><Label className="text-xs">Base Cost / COGS</Label><Input type="number" step="0.01" value={form.base_cost} onChange={(e) => setForm({...form, base_cost: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
              <div>
                <Label className="text-xs">GST %</Label>
                <Select value={String(form.gst_percent)} onValueChange={(v) => setForm({...form, gst_percent: parseFloat(v)})}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="sku-gst-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{GST_RATES_LIST.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">HSN Code</Label><Input value={form.hsn_code} onChange={(e) => setForm({...form, hsn_code: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Barcode / EAN</Label><Input value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Shelf Life (days)</Label><Input type="number" value={form.shelf_life_days} onChange={(e) => setForm({...form, shelf_life_days: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({...form, reorder_level: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="sku-reorder-input" /></div>
              <div><Label className="text-xs">MOQ</Label><Input type="number" value={form.moq} onChange={(e) => setForm({...form, moq: parseInt(e.target.value) || 1})} className="mt-1 rounded-sm" /></div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SKU_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-sku-button">Save SKU</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
